import React from 'react';
import { Dropdown } from 'react-bootstrap';
import { Lock, LockOpen } from 'lucide-react';

const fiscalYearKey = (fiscalYear) => String(
  fiscalYear?.fiscal_year_id || fiscalYear?.year || fiscalYear?.name || ''
);

const fiscalYearLabel = (fiscalYear) => fiscalYear?.year || fiscalYear?.name || 'Okänt år';

export const sortFiscalYears = (years) => [...years].sort((a, b) => (
  String(fiscalYearLabel(b)).localeCompare(String(fiscalYearLabel(a)), 'sv', { numeric: true })
));

const FiscalYearMoreToggle = React.forwardRef(({ onClick, disabled }, ref) => (
  <button
    ref={ref}
    type="button"
    disabled={disabled}
    aria-label="Visa fler räkenskapsår"
    onClick={(event) => {
      event.preventDefault();
      if (!disabled) onClick(event);
    }}
    className="ll-chip"
    style={{ letterSpacing: '0.1em', opacity: disabled ? 0.6 : 1 }}
  >
    •••
  </button>
));

const FiscalYearPicker = ({ years, selectedYear, onChange, disabled = false }) => {
  const sortedYears = sortFiscalYears(years || []);
  const newestYears = sortedYears.slice(0, 2);
  const selectedKey = fiscalYearKey(selectedYear);
  const selectedIsOlder = selectedKey && !newestYears.some((year) => fiscalYearKey(year) === selectedKey);
  const visibleYears = selectedIsOlder
    ? [newestYears[0], selectedYear].filter(Boolean)
    : newestYears;
  const visibleKeys = new Set(visibleYears.map(fiscalYearKey));
  const remainingYears = sortedYears.filter((year) => !visibleKeys.has(fiscalYearKey(year)));

  return (
    <div className="d-flex align-items-center flex-wrap gap-2" role="group" aria-label="Välj räkenskapsår">
      <span style={{ color: '#475569', fontSize: '0.875rem', fontWeight: 600 }}>Räkenskapsår</span>
      {visibleYears.map((year) => {
        const key = fiscalYearKey(year);
        const isSelected = key === selectedKey;
        const isLocked = year.state === 'closed';
        const StatusIcon = isLocked ? Lock : LockOpen;
        const statusLabel = isLocked ? 'Låst' : 'Öppet';
        return (
          <button
            key={key}
            type="button"
            aria-pressed={isSelected}
            disabled={disabled}
            onClick={() => onChange(year)}
            className={`ll-chip ${isSelected ? 'active' : ''}`}
            style={{ opacity: disabled ? 0.6 : 1 }}
            title={`${fiscalYearLabel(year)} – ${statusLabel}`}
          >
            <StatusIcon size={13} className="ll-chip__icon" aria-hidden="true" />
            {fiscalYearLabel(year)}
            <span className="visually-hidden">, {statusLabel}</span>
          </button>
        );
      })}
      {remainingYears.length > 0 && (
        <Dropdown align="end">
          <Dropdown.Toggle as={FiscalYearMoreToggle} disabled={disabled} />
          <Dropdown.Menu>
            <Dropdown.Header>Fler räkenskapsår</Dropdown.Header>
            {remainingYears.map((year) => {
              const isSelected = fiscalYearKey(year) === selectedKey;
              const isLocked = year.state === 'closed';
              const StatusIcon = isLocked ? Lock : LockOpen;
              return (
                <Dropdown.Item
                  key={fiscalYearKey(year)}
                  active={isSelected}
                  onClick={() => onChange(year)}
                  className="d-flex align-items-center gap-2"
                  style={isSelected ? { background: 'var(--primary)', color: '#fff' } : {}}
                >
                  <StatusIcon size={14} aria-hidden="true" />
                  {fiscalYearLabel(year)}
                </Dropdown.Item>
              );
            })}
          </Dropdown.Menu>
        </Dropdown>
      )}
    </div>
  );
};

export default FiscalYearPicker;
